import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { socialApi } from '../../api/social';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { toast } from 'sonner';

export default function FollowSuggestionsCard({ variant, onBrowseDiscover }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    socialApi.getFollowSuggestions({ limit: 5 })
      .then(res => { if (!cancelled) setSuggestions(res.data.suggestions || []); })
      .catch(() => { if (!cancelled) setSuggestions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleFollow = async (userId) => {
    setFollowingIds(prev => new Set(prev).add(userId));
    try {
      await socialApi.followUser(userId, ['workout', 'meal', 'checkin']);
      setTimeout(() => {
        setSuggestions(prev => prev.filter(s => s.id !== userId));
        setFollowingIds(prev => {
          const next = new Set(prev); next.delete(userId); return next;
        });
      }, 300);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('social.errorFollow'));
      setFollowingIds(prev => {
        const next = new Set(prev); next.delete(userId); return next;
      });
    }
  };

  if (loading) return null;

  const title = variant === 'empty'
    ? t('social.feedEmptyTitle')
    : t('social.feedCaughtUpTitle');

  return (
    <Card className="border-border/50 bg-gradient-to-br from-muted/80 to-muted/30">
      <CardContent className="p-4 space-y-3">
        <div>
          <div className="text-base font-semibold">{title}</div>
          <div className="text-sm text-muted-foreground">
            {t('social.followSuggestionsSubtext')}
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
            {suggestions.map(s => {
              const isFollowing = followingIds.has(s.id);
              return (
                <div
                  key={s.id}
                  className={`flex-shrink-0 w-36 rounded-xl border border-border/60 bg-background/70 p-3 transition-opacity ${isFollowing ? 'opacity-40' : 'opacity-100'}`}
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/profile/${s.id}`)}
                    className="flex flex-col items-center gap-2 w-full"
                  >
                    <Avatar className="h-14 w-14">
                      <AvatarImage src={s.profile_picture} alt={s.name} />
                      <AvatarFallback>{(s.name || '?')[0]}</AvatarFallback>
                    </Avatar>
                    <div className="text-sm font-medium truncate max-w-full">{s.name}</div>
                    {s.username && (
                      <div className="text-xs text-muted-foreground truncate max-w-full">@{s.username}</div>
                    )}
                  </button>
                  <Button
                    size="sm"
                    className="w-full mt-2"
                    disabled={isFollowing}
                    onClick={() => handleFollow(s.id)}
                  >
                    {t('social.followButton')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <Button variant="outline" className="w-full" onClick={onBrowseDiscover}>
          {t('social.browseDiscover')}
        </Button>
      </CardContent>
    </Card>
  );
}
